import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Award, Download, Loader2, Lock, ShieldCheck, ArrowLeft } from "lucide-react";
import api, { API } from "@/lib/api";

export default function ServiceNowCertificatePage() {
  const [eligibility, setEligibility] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [eligRes, certRes] = await Promise.all([
        api.get("/servicenow/certificate/eligibility"),
        api.get("/servicenow/certificate/me"),
      ]);
      setEligibility(eligRes.data);
      setCertificate(certRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/servicenow/certificate/generate");
      setCertificate(data);
      toast.success("Certificate generated!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to generate certificate yet");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="servicenow-certificate-loading">
        <div className="w-10 h-10 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="servicenow-certificate-page">
      <Link to="/servicenow" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to ServiceNow Roadmap
      </Link>

      <div className="glass-panel rounded-2xl p-8 text-center" data-testid="servicenow-certificate-panel">
        <Award className="w-14 h-14 text-[#4ade80] mx-auto mb-4" />
        <h1 className="font-heading text-2xl font-semibold text-white">ServiceNow 30-Day Certificate</h1>

        {certificate ? (
          <div className="mt-6" data-testid="servicenow-certificate-ready">
            <p className="text-sm text-emerald-400 font-semibold flex items-center justify-center gap-1.5"><ShieldCheck className="w-4 h-4" /> Certificate Ready</p>
            <p className="text-xs text-slate-400 mt-2">ID: {certificate.certificate_id}</p>
            <p className="text-xs text-slate-400">Score: {certificate.score}%</p>
            <a
              href={`${API}/servicenow/certificate/${certificate.certificate_id}/pdf`} data-testid="servicenow-certificate-download-button"
              className="inline-flex items-center gap-2 mt-5 bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" /> Download Certificate PDF
            </a>
            <p className="text-[11px] text-slate-500 mt-3">Verify at: {certificate.verification_url}</p>
          </div>
        ) : eligibility?.eligible ? (
          <div className="mt-6" data-testid="servicenow-certificate-eligible">
            <p className="text-sm text-slate-300">You've completed all {eligibility.total_days} days with an average score of {eligibility.avg_score}%. Generate your certificate now!</p>
            <button
              onClick={generate} disabled={generating} data-testid="servicenow-certificate-generate-button"
              className="inline-flex items-center gap-2 mt-5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-white text-sm font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />} Generate Certificate
            </button>
          </div>
        ) : (
          <div className="mt-6" data-testid="servicenow-certificate-locked">
            <Lock className="w-6 h-6 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-400">
              Complete all {eligibility?.total_days ?? 30} days (currently {eligibility?.completed_count ?? 0}) with an average quiz score of at least {eligibility?.passing_score ?? 80}% to unlock your certificate.
            </p>
            <Link to="/servicenow" className="inline-block mt-4 text-emerald-400 hover:underline text-sm">Continue the Challenge →</Link>
          </div>
        )}
      </div>
    </div>
  );
}
